import React from "react";

/**
 * Página de Política de Privacidad para Calypso (Bonzzai Enterprise).
 *
 * CÓMO INTEGRARLA:
 * 1. Guarda este archivo como src/pages/PoliticaPrivacidad.jsx (o donde tengas tus páginas).
 * 2. En tu router (App.js / rutas principales), agrega:
 *      import PoliticaPrivacidad from './pages/PoliticaPrivacidad';
 *      <Route path="/privacidad" element={<PoliticaPrivacidad />} />
 * 3. Haz build + deploy en Cloudflare Pages como siempre.
 * 4. Verifica que https://bonzzaienterprise.com/privacidad cargue bien (recuerda que
 *    ya tienes configurado el SPA routing en wrangler.toml, así que debería andar directo).
 * 5. Pega esa URL en Play Console > Política de la app > Política de privacidad.
 *
 * IMPORTANTE: Revisa y ajusta los textos marcados con [REVISAR] antes de publicar.
 * Esto es un punto de partida sólido, no asesoría legal formal.
 */
export default function PoliticaPrivacidad() {
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.h1}>Política de Privacidad de Calypso</h1>
        <p style={styles.updated}>Última actualización: agosto de 2026</p>

        <p style={styles.p}>
          Bonzzai Enterprise ("nosotros") desarrolla y opera la aplicación
          Calypso ("la App"). Esta política explica qué información
          recolectamos, cómo la usamos, y qué derechos tienes sobre tus datos.
        </p>

        <h2 style={styles.h2}>1. Información que recolectamos</h2>
        <ul style={styles.ul}>
          <li>
            <strong>Datos de cuenta:</strong> correo electrónico, nombre y,
            opcionalmente, número de cédula (usado únicamente para la
            funcionalidad de Declaración de Renta).
          </li>
          <li>
            <strong>Datos financieros:</strong> ingresos, gastos, categorías,
            fechas y montos que registras manualmente o importas por CSV.
            Estos datos se almacenan <strong>cifrados (AES)</strong> en
            nuestra base de datos.
          </li>
          <li>
            <strong>Imágenes de facturas:</strong> si usas el escáner de
            facturas, la imagen se envía a la API de Anthropic (Claude)
            únicamente para extraer los datos de la factura (monto, fecha,
            categoría). Bajo los términos comerciales de Anthropic, esta
            información no se usa para entrenar sus modelos y se elimina
            automáticamente de sus servidores dentro de un plazo de 30 días,
            salvo que la ley exija un periodo mayor.
          </li>
          <li>
            <strong>Mensajes del chatbot financiero:</strong> las preguntas
            que le haces al asistente de IA, junto con un resumen de tu
            contexto financiero (ingresos, gastos y categorías), se envían a
            la API de Anthropic (Claude) para generar una respuesta
            personalizada. Al igual que con el escáner de facturas, esta
            información no se usa para entrenar modelos de Anthropic y se
            elimina de sus servidores dentro de un plazo de 30 días.
          </li>
          <li>
            <strong>Datos de pago/suscripción:</strong> gestionados por
            Google Play Billing (Android) o Wompi (web). No almacenamos
            números de tarjeta; estos son manejados directamente por dichos
            procesadores.
          </li>
        </ul>

        <h2 style={styles.h2}>2. Cómo usamos tu información</h2>
        <ul style={styles.ul}>
          <li>Brindarte las funciones de la App (seguimiento financiero, alertas, análisis, chatbot).</li>
          <li>Procesar tu suscripción premium.</li>
          <li>Enviarte notificaciones y alertas que configures dentro de la App.</li>
          <li>Mejorar la seguridad y el funcionamiento de la App.</li>
          <li>Cumplir obligaciones legales cuando aplique.</li>
        </ul>

        <h2 style={styles.h2}>3. Con quién compartimos datos</h2>
        <p style={styles.p}>No vendemos tus datos. Compartimos información únicamente con:</p>
        <ul style={styles.ul}>
          <li><strong>Supabase</strong> — almacenamiento de base de datos y autenticación.</li>
          <li><strong>RevenueCat / Google Play Billing</strong> — gestión de suscripciones en Android.</li>
          <li><strong>Wompi</strong> — procesamiento de pagos en la versión web.</li>
          <li>
            <strong>Anthropic (Claude API)</strong> — procesamiento del
            escáner de facturas y del chatbot financiero. Bajo el Acuerdo de
            Servicios Comerciales de Anthropic, los datos enviados vía API no
            se usan para entrenar sus modelos por defecto y se eliminan
            automáticamente de sus servidores en un plazo de 30 días desde
            su recepción. Puedes consultar los términos vigentes en{" "}
            <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noopener noreferrer">
              anthropic.com/legal/commercial-terms
            </a>{" "}
            y su política de privacidad en{" "}
            <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">
              anthropic.com/legal/privacy
            </a>.
          </li>
          <li><strong>Resend</strong> — envío de correos transaccionales (verificación, notificaciones).</li>
        </ul>

        <h2 style={styles.h2}>4. Almacenamiento y seguridad</h2>
        <p style={styles.p}>
          Tus datos financieros sensibles se almacenan cifrados. Usamos
          conexiones seguras (HTTPS) en toda la App y aplicamos controles de
          acceso a nivel de base de datos para proteger tu información.
        </p>

        <h2 style={styles.h2}>5. Tus derechos</h2>
        <p style={styles.p}>Puedes en cualquier momento:</p>
        <ul style={styles.ul}>
          <li>Acceder a tus datos desde tu perfil dentro de la App.</li>
          <li>Corregir tu información personal.</li>
          <li>
            <strong>Solicitar la eliminación de tu cuenta y tus datos</strong>{" "}
            directamente desde la App en{" "}
            <a href="/eliminar-cuenta">bonzzaienterprise.com/eliminar-cuenta</a>
            , eliminar datos específicos sin borrar tu cuenta en{" "}
            <a href="/eliminar-datos">bonzzaienterprise.com/eliminar-datos</a>
            , o escribiendo a{" "}
            <a href="mailto:soporte@bonzzaienterprise.com">
              soporte@bonzzaienterprise.com
            </a>
            . Procesaremos la solicitud en un plazo razonable.
          </li>
        </ul>

        <h2 style={styles.h2}>6. Menores de edad</h2>
        <p style={styles.p}>
          Calypso no está dirigida a menores de 18 años y no recolectamos
          intencionalmente datos de menores.
        </p>

        <h2 style={styles.h2}>7. Cambios a esta política</h2>
        <p style={styles.p}>
          Podemos actualizar esta política ocasionalmente. Notificaremos
          cambios importantes dentro de la App o por correo electrónico.
        </p>

        <h2 style={styles.h2}>8. Contacto</h2>
        <p style={styles.p}>
          Si tienes preguntas sobre esta política o sobre tus datos, escríbenos a{" "}
          <a href="mailto:soporte@bonzzaienterprise.com">
            soporte@bonzzaienterprise.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    padding: "40px 20px",
    background: "#ffffff",
    color: "#1a1a1a",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  content: {
    maxWidth: "720px",
    margin: "0 auto",
  },
  h1: {
    fontSize: "28px",
    marginBottom: "4px",
  },
  h2: {
    fontSize: "20px",
    marginTop: "32px",
    marginBottom: "8px",
  },
  updated: {
    color: "#666",
    fontSize: "14px",
    marginBottom: "24px",
  },
  p: {
    lineHeight: "1.6",
    marginBottom: "12px",
  },
  ul: {
    lineHeight: "1.6",
    paddingLeft: "20px",
    marginBottom: "12px",
  },
};
